import { useEffect } from 'react';
import { Card, List, Button, Tag, message } from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { OAuthProvider } from '@nagoya-cat/shared';
import { api, linkWith } from '../api/client';

const SSO: { key: OAuthProvider; label: string; avatar: string }[] = [
  { key: 'line', label: 'LINE', avatar: 'bg-[#06C755]' },
  { key: 'google', label: 'Google', avatar: 'bg-[#4285F4]' },
];

/** 我的登入方式（綁定多 SSO，限 LINE / Google）— 放在帳號管理頁，可直接點綁定。 */
export default function MyLogins() {
  const qc = useQueryClient();
  const ids = useQuery({ queryKey: ['identities'], queryFn: api.identities });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('linked')) message.success('已綁定登入方式');
    if (p.get('error')) message.error(decodeURIComponent(p.get('error')!));
    if (p.get('linked') || p.get('error')) window.history.replaceState({}, '', '/users');
  }, []);

  const unlink = useMutation({
    mutationFn: (p: OAuthProvider) => api.unlinkProvider(p),
    onSuccess: () => {
      message.success('已解除');
      qc.invalidateQueries({ queryKey: ['identities'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const linked = new Map((ids.data ?? []).map((i) => [i.provider, i.email]));

  return (
    <Card title="我的登入方式（可綁定多種 SSO）" className="mb-4" loading={ids.isLoading}>
      <List
        dataSource={SSO}
        renderItem={(p) => {
          const on = linked.has(p.key);
          return (
            <List.Item
              actions={[
                on ? (
                  <Button
                    key="u"
                    danger
                    size="small"
                    icon={<DisconnectOutlined />}
                    loading={unlink.isPending}
                    disabled={(ids.data?.length ?? 0) <= 1}
                    onClick={() => unlink.mutate(p.key)}
                  >
                    解除
                  </Button>
                ) : (
                  <Button key="l" type="primary" ghost size="small" icon={<LinkOutlined />} onClick={() => linkWith(p.key)}>
                    綁定
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                avatar={
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full font-bold text-white ${p.avatar}`}>
                    {p.label[0]}
                  </span>
                }
                title={p.label}
                description={
                  on ? (
                    <Tag color="green">已綁定{linked.get(p.key) ? ` · ${linked.get(p.key)}` : ''}</Tag>
                  ) : (
                    <span className="text-gray-400">未綁定</span>
                  )
                }
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
}
